/**
 * Utility function to detect changes between original and current data.
 * @param {Object} originalData - Original data object
 * @param {Object} currentData - Current data object
 * @param {Array} ignoreFields - Array of field names to ignore during comparison
 * @returns {boolean} True if changes detected, false otherwise.
 */
export const detectChanges = (originalData, currentData, ignoreFields = []) => {
    // Handle null/undefined cases.
    if (!originalData && !currentData) return false;
    if (!originalData || !currentData) return true;

    // Create copies to avoid mutating original objects.
    const original = { ...originalData };
    const current = { ...currentData };

    // Remove ignored fields from comparison.
    ignoreFields.forEach((field) => {
        delete original[field];
        delete current[field];
    });

    // Deep comparison using JSON.stringify
    return JSON.stringify(original) !== JSON.stringify(current);
};

/**
 * Utility function to detect changes with initial state comparison.
 * @param {Object} originalData - Original data object (can be null for new items).
 * @param {Object} currentData - Current data object.
 * @param {Object} initialState - Initial state object for new items.
 * @param {Array} ignoreFields - Array of field names to ignore during comparison.
 * @returns {boolean} True if changes detected, false otherwise.
 */
export const detectChangesWithInitialState = (originalData, currentData, initialState, ignoreFields = []) => {
    // If originalData exists, compare with current data.
    if (originalData) {
        return detectChanges(originalData, currentData, ignoreFields);
    }

    // If no originalData (new item), compare with initial state.
    return detectChanges(initialState, currentData, ignoreFields);
};

/**
 * Utility function to show confirmation dialog for unsaved changes.
 * @param {boolean} hasChanges - Whether changes are detected.
 * @param {string} message - Custom confirmation message.
 * @returns {boolean} True if user confirms, false otherwise.
 */
export const confirmUnsavedChanges = (
    hasChanges,
    message = 'Unsaved changes will be lost. Do you want to continue?'
) => {
    if (!hasChanges) return true;
    return window.confirm(message);
};

/**
 * Utility function to handle dialog close with change detection.
 * @param {Object} originalData - Original data object
 * @param {Object} currentData - Current data object
 * @param {Object} initialState - Initial state object
 * @param {Function} onConfirm - Function to call if user confirms
 * @param {Function} onCancel - Function to call if user cancels
 * @param {Array} ignoreFields - Array of field names to ignore.
 * @param {string} message - Custom confirmation message.
 */
export const handleDialogCloseWithDetection = (
    originalData,
    currentData,
    initialState,
    onConfirm,
    onCancel = () => {},
    ignoreFields = [],
    message = 'Unsaved changes will be lost. Do you want to continue?'
) => {
    const hasChanges = detectChangesWithInitialState(originalData, currentData, initialState, ignoreFields);

    if (confirmUnsavedChanges(hasChanges, message)) {
        onConfirm();
    } else {
        onCancel();
    }
};
